import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { useStore } from '../store/useStore';
import api, {
  impersonateUser,
  getAuthToken, setAuthToken,
  getCurrentUserId, setCurrentUserId,
  setImpersonator,
} from '../utils/api';
import styles from './AdminUsersPanel.module.css';

// Admin Console → Users panel. Promote/demote, add new users
// manually, deactivate/restore. Lives inside AdminConsole's left-nav
// shell; renders without an outer page wrapper since the shell
// already provides scroll. The real authorization gate is on the
// server (/api/users/* is admin-only); this UI just hides itself
// for non-admins as a UX courtesy.

const ROLES = [
  { value: 'admin', label: 'Admin',           sub: 'Full access incl. user management' },
  { value: 'pm',    label: 'Project Manager', sub: 'Edit projects, sheets, files, mixes' },
  { value: 'super', label: 'Super',           sub: 'Photos, markups, daily reports — no drawing edits' },
  { value: 'field', label: 'Field Staff',     sub: 'Read-only; private markups for personal notes' },
];
const ROLE_COLORS = {
  admin: 'var(--accent)',
  pm:    'var(--blue)',
  super: 'var(--green)',
  field: 'var(--text-dim)',
};

export default function AdminUsersPanel() {
  const role        = useStore((s) => s.role);
  const currentUser = useStore((s) => s.currentUser);

  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole,  setNewRole]  = useState('field');

  useEffect(() => {
    if (role !== 'admin') return;
    refresh();
    // eslint-disable-next-line
  }, [role, showDeactivated]);

  async function refresh() {
    setLoading(true);
    try {
      const params = showDeactivated ? '?include_deleted=1' : '';
      const list = await api.get('/users' + params);
      setUsers(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  async function changeRole(u, role) {
    setSavingId(u.id);
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.name || u.email} → ${role}`);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to update role');
    } finally {
      setSavingId(null);
    }
  }

  async function deactivate(u) {
    if (!window.confirm(`Deactivate ${u.email}? They'll lose access immediately. You can restore them later.`)) return;
    setSavingId(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.email} deactivated`);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate');
    } finally {
      setSavingId(null);
    }
  }

  async function restore(u) {
    setSavingId(u.id);
    try {
      await api.post(`/users/${u.id}/restore`);
      toast.success(`${u.email} restored`);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to restore');
    } finally {
      setSavingId(null);
    }
  }

  async function addUser(e) {
    e.preventDefault();
    setAdding(true);
    try {
      const created = await api.post('/users', {
        email: newEmail.trim().toLowerCase(),
        role:  newRole,
      });
      // Server attempts to send the invite as part of the create call;
      // surface whichever leg succeeded so the admin knows whether to
      // hit Resend Invite manually.
      if (created.invite_sent) {
        toast.success(`Invitation sent to ${created.email}`);
      } else {
        toast.error(`User ${created.email} added but invite email failed — try Resend.`);
      }
      setNewEmail(''); setNewRole('field');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to add user');
    } finally {
      setAdding(false);
    }
  }

  async function resendInvite(u) {
    setSavingId(u.id);
    try {
      await api.post(`/users/${u.id}/invite`);
      toast.success(`Invitation resent to ${u.email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to resend invite');
    } finally {
      setSavingId(null);
    }
  }

  // "Log in as" — request a fresh JWT for the target user and swap it
  // into our own localStorage. The original admin JWT is stashed under
  // `cf.impersonator` so the banner in App.jsx can offer a one-click
  // "Return to <admin>" path without forcing a re-login. A hard reload
  // re-bootstraps the entire app under the target user's identity, so
  // every cached store field, role check, and project list reflects
  // their reality — which is what makes this useful for testing role
  // gates.
  async function loginAs(u) {
    if (!window.confirm(
      `Log in as ${u.name || u.email}? You'll see exactly what they see; ` +
      `use the banner at the top to return to your admin session.`
    )) return;
    setSavingId(u.id);
    try {
      const { token, user } = await impersonateUser(u.id);
      const originalToken = getAuthToken();
      const originalUserId = getCurrentUserId();
      setImpersonator({
        originalToken,
        originalUserId,
        originalName: currentUser?.name || currentUser?.email || 'admin',
        targetName:   user?.name || user?.email || u.email,
        startedAt:    new Date().toISOString(),
      });
      setAuthToken(token);
      setCurrentUserId(user.id);
      // Hard reload — every per-user cache (store, role, projects list,
      // active project membership) needs to re-fetch under the new JWT.
      // A soft state update would leave stale `currentUser` / projects
      // around for the duration of the impersonation session.
      window.location.reload();
    } catch (err) {
      toast.error(err.message || 'Failed to start impersonation');
      setSavingId(null);
    }
  }

  // Memoize BEFORE the early return so React's hook order stays
  // consistent across renders (rules-of-hooks).
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      // Active first, then deactivated. Within each, by name.
      if (!!a.deleted_at !== !!b.deleted_at) return a.deleted_at ? 1 : -1;
      return (a.name || a.email).localeCompare(b.name || b.email);
    });
  }, [users]);

  // Client-side guard. The real gate is on the server, but rendering
  // the page for non-admins would just splash a 403 toast on every
  // refresh() call — show a clear message instead.
  if (role !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          Admins only. If you think you should have access, ask the project admin.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Manage Users</h1>
          <div className={styles.sub}>
            {users.filter((u) => !u.deleted_at).length} active
            {showDeactivated && users.some((u) => u.deleted_at) && (
              <> · {users.filter((u) => u.deleted_at).length} deactivated</>
            )}
          </div>
        </div>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={showDeactivated}
            onChange={(e) => setShowDeactivated(e.target.checked)}
          />
          <span>Show deactivated</span>
        </label>
      </header>

      <section className={styles.addCard}>
        <div className={styles.addTitle}>Invite a user</div>
        <form
          className={styles.addForm}
          onSubmit={addUser}
          style={{ gridTemplateColumns: '2fr 160px 140px' }}
        >
          <input
            className={styles.input}
            type="email"
            placeholder="email@domain"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
          />
          <select
            className={styles.input}
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            type="submit"
            className={styles.addBtn}
            disabled={adding || !newEmail.trim()}
          >
            {adding ? 'Sending…' : 'Send invite'}
          </button>
        </form>
        <div className={styles.addHint}>
          Sends an email with a one-click setup link. The invitee fills in
          their first / last name and phone, then creates a password — and
          uses email + password to sign in from then on.
        </div>
      </section>

      <div className={styles.tableWrap}>
        {loading && <div className={styles.empty}>Loading…</div>}
        {!loading && sortedUsers.length === 0 && (
          <div className={styles.empty}>No users.</div>
        )}
        {!loading && sortedUsers.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => {
                const isSelf = u.id === currentUser?.id;
                const deactivated = !!u.deleted_at;
                return (
                  <tr key={u.id} className={deactivated ? styles.deactivatedRow : ''}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span className={styles.roleDot} style={{ background: ROLE_COLORS[u.role] }} />
                        {/* Prefer first+last; fall back to legacy `name`
                            for users created before the schema split. */}
                        {(u.first_name || u.last_name)
                          ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                          : (u.name || <span className={styles.muted}>—</span>)}
                        {isSelf && <span className={styles.selfBadge}>YOU</span>}
                        {deactivated && <span className={styles.deactivatedBadge}>DEACTIVATED</span>}
                        {!deactivated && u.password_set === false && (
                          <span className={styles.selfBadge} style={{ color: 'var(--yellow, #b45309)' }}>
                            NO PW YET
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={styles.mono}>{u.email}</td>
                    <td className={styles.mono}>
                      {u.phone || <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      {deactivated ? (
                        <span className={styles.muted} style={{ color: ROLE_COLORS[u.role], fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          {u.role}
                        </span>
                      ) : (
                        <select
                          className={styles.roleSelect}
                          value={u.role}
                          disabled={savingId === u.id || (isSelf && u.role === 'admin')}
                          onChange={(e) => changeRole(u, e.target.value)}
                          style={{ color: ROLE_COLORS[u.role] }}
                          title={isSelf && u.role === 'admin' ? "Admins can't demote themselves" : ''}
                        >
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className={styles.mono}>
                      {u.created_at ? format(parseISO(u.created_at), 'MMM d, yyyy') : <span className={styles.muted}>—</span>}
                    </td>
                    <td className={styles.actionsCol}>
                      <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        {!deactivated && !isSelf && (
                          <button
                            className={styles.btnImpersonate}
                            onClick={() => loginAs(u)}
                            disabled={savingId === u.id}
                            title={`See the app exactly as ${u.name || u.email} would — for testing role gates`}
                          >Log in as</button>
                        )}
                        {!deactivated && (
                          <button
                            className={styles.btnDeactivate}
                            onClick={() => resendInvite(u)}
                            disabled={savingId === u.id}
                            style={{ borderColor: 'var(--border2)' }}
                            title={u.password_set
                              ? 'Resend invite — also acts as a password reset'
                              : 'Resend the setup invitation'}
                          >{u.password_set ? 'Reset' : 'Resend'}</button>
                        )}
                        {deactivated ? (
                          <button
                            className={styles.btnRestore}
                            onClick={() => restore(u)}
                            disabled={savingId === u.id}
                          >Restore</button>
                        ) : (
                          <button
                            className={styles.btnDeactivate}
                            onClick={() => deactivate(u)}
                            disabled={savingId === u.id || isSelf}
                            title={isSelf ? "You can't deactivate yourself" : ''}
                          >Deactivate</button>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
